import { useCallback, useSyncExternalStore } from "react";
import { createSpellingStore } from "@/shared/spellcheck/store";
import { logger } from "@/shared/utils/logger";

const spelling = createSpellingStore(
	() =>
		new Worker(new URL("../spellcheck/spellcheck.worker.ts", import.meta.url), {
			type: "module",
		}),
	() =>
		logger.captureException(new Error("Narrative spellchecker unavailable"), {
			action: "spellcheck",
		}),
);
const getServerSnapshot = () => undefined;

export function SpellcheckedNarrative({
	text,
	className,
	debounceMs = 0,
}: {
	text: string;
	className?: string;
	/** Wait for generated text to settle before checking it. */
	debounceMs?: number;
}) {
	const subscribe = useCallback(
		(notify: () => void) => {
			if (!debounceMs) return spelling.subscribe(text, notify);
			let unsubscribe: (() => void) | undefined;
			const timeout = setTimeout(() => {
				unsubscribe = spelling.subscribe(text, notify);
				notify();
			}, debounceMs);
			return () => {
				clearTimeout(timeout);
				unsubscribe?.();
			};
		},
		[text, debounceMs],
	);
	const getSnapshot = useCallback(() => spelling.getSnapshot(text), [text]);
	const ranges = useSyncExternalStore(
		subscribe,
		getSnapshot,
		getServerSnapshot,
	);
	let cursor = 0;
	const segments = ranges?.map(({ start, end }) => {
		const prefix = text.slice(cursor, start);
		cursor = end;
		return (
			<span key={start}>
				{prefix}
				<span
					aria-invalid="spelling"
					className="underline decoration-wavy decoration-red-500 underline-offset-[3px]"
				>
					{text.slice(start, end)}
				</span>
			</span>
		);
	});
	return (
		<p className={className}>
			{segments}
			{text.slice(cursor)}
		</p>
	);
}
